import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import char from '../../assets/images/intake/step13/char.svg';
import { useSkillsContext } from '../../hooks/use-form-context.hooks';
import { ConfidenceLevel, dispatchUpdateSkills } from '../../redux/slice/form-skills';
import { useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';

import { skillOptions } from './Communication';

interface ComponentProps {
  step: number;
  onBack: () => void;
}

interface FormData {
  skillTeamwork: string;
}

export const TeamWork: React.FC<ComponentProps> = ({ step, onBack }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { skillTeamwork } = useSkillsContext();
  const [error, setError] = useState('');
  const [selectedOption, setSelectedOption] = useState(skillTeamwork || '');
  const { handleSubmit } = useForm<FormData>();

  const backButtonClick = () => {
    onBack();
  };
  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(parseInt(event.target.value));
    setError('');
  };
  const submitForm: SubmitHandler<FormData> = async () => {
    logger.debug('Submitted skill teamwork form.', selectedOption);

    const error_message: string = 'Please select an option';
    if (selectedOption === undefined) {
      setError(error_message);
    } else {
      const dto: Record<keyof FormData, ConfidenceLevel> = {
        skillTeamwork: selectedOption as ConfidenceLevel,
      };
      await dispatch(dispatchUpdateSkills(dto));
      navigate(`/pre-employment/skills/step${step + 1}`);
    }
  };

  useEffect(() => {
    if (skillTeamwork) {
      setSelectedOption(skillTeamwork);
    }
  }, [skillTeamwork]);

  return (
    <div>
      <div className="flex mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">18</div>
        <div>
          <h4 className="text-2xl">
            <span className="font-semibold">How comfortable are you </span>
            with each of these competencies
          </h4>
        </div>
      </div>
      <div className="character absolute top-96 right-[-125px] z-[-1]">
        <img className="" src={char} alt="" />
      </div>
      <form
        className=" md:px-10"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(submitForm)}
      >
        <h4 className="mb-1 font-semibold text-purple-700 text-base">Competency</h4>
        <h3 className="font-semibold text-black text-xl mb-8">Teamwork</h3>
        <h4 className="mb-2 font-semibold text-purple-700 text-base">Description</h4>
        <p className="mb-6">
          Build and maintain collaborative relationships to work effectively with others in a team
          setting through shared responsibility, empathy and respect.
        </p>
        <h4 className="mb-1 font-semibold text-purple-700 text-base mb-3">
          Competency items to be rated
        </h4>
        <ul className="list-disc pl-6">
          <li className="mb-2">Able and willing to share and receive information.</li>
          <li className="mb-2">Encourage and respect the input of other team members.</li>
          <li className="mb-2">Communicate openly and honestly.</li>
          <li className="mb-2">Co-operate within the group and across groups.</li>
          <li className="mb-2">Support group decisions and put group goals ahead of own goals.</li>
          <li className="mb-2">
            Commit to the common goal and fulfill individual responsibilities.
          </li>
          <li className="mb-2">Handle difficult conversations with respect.</li>
        </ul>
        <div className="rating-btns flex items-center mt-10">
          {skillOptions.map((option) => (
            <div key={option.label} className="h-[55px] mr-5">
              <label className="radio-container h-full block relative">
                <input
                  checked={selectedOption === option.value}
                  onChange={handleOptionChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  name="communication"
                  value={option.value}
                />
                <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                  {option.label}
                </span>
              </label>
            </div>
          ))}
        </div>
        {error && <p className="text-red-500 font-light !text-sm pt-5">{error}</p>}
        <div className="flex justify-between mt-20">
          <button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(backButtonClick)}
            className="btn-primary"
          >
            Back
          </button>
          <button className="btn-primary">Next</button>
        </div>
      </form>
    </div>
  );
};
